import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getPageContactEmail } from "./siteContactConfig";
import fispLogo from "../../assets/logo-iniciales-oscuro.png";

const NAV_COLLAPSE_WIDTH = 824;
const CAMPUS_URL = "https://fispcampus.com/autogestion/login/index.php";
const CONTACT_PHONE_LABEL = "+54 9 11-2387-9118";
const CONTACT_PHONE_HREF = "https://wa.me/5491123879118";

const NAV_ITEMS = [
    {
        id: "educativo",
        label: "FISP Educativo",
        to: "/educativo",
        menuTitle: "Oferta académica",
        menu: [
            { label: "Posgrados", to: "/educativo?categoria=posgrado&scroll=catalogo" },
            { label: "Diplomaturas", to: "/educativo?categoria=diplomatura&scroll=catalogo" },
            { label: "Cursos", to: "/educativo?categoria=curso&scroll=catalogo" },
        ],
    },
    {
        id: "tv",
        label: "FISP TV",
        to: "/fisp-tv",
    },
    {
        id: "servicios",
        label: "Servicios Empresariales",
        to: "/servicios",
        menuTitle: "Servicios",
        menu: [
            { label: "Wellness Corporativo", to: "/servicios/wellness-corporativo" },
            { label: "Políticas Públicas e Inteligencia Institucional", to: "/servicios/politicas-publicas-inteligencia-institucional" },
            { label: "Proyectos Tecnológicos en Salud", to: "/servicios/proyectos-tecnologicos" },
            { label: "Marketing y Publicidad en Salud", to: "/servicios/marketing-publicidad" },
            { label: "Servicios Médicos", to: "/servicios/servicios-medicos" },
            { label: "Producción y Cobertura de Eventos", to: "/servicios/produccion-eventos" },
            { label: "Capacitación Laboral", to: "/servicios/capacitacion-laboral" },
        ],
    },
    {
        id: "fundacion",
        label: "La Fundación",
        to: "/fundacion",
    },
];

function isProgramDetailPath(pathname) {
    return /^\/(cursos|diplomaturas|posgrados)\/[^/]+$/.test(pathname);
}

/* ─── Icons ─── */
function Chevron({ className = "h-3 w-3" }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className={className} aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
        </svg>
    );
}
function MenuIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5" aria-hidden="true">
            <path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" />
        </svg>
    );
}
function CloseIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5" aria-hidden="true">
            <path d="m6 6 12 12" /><path d="m18 6-12 12" />
        </svg>
    );
}
function LockIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" aria-hidden="true">
            <rect x="5" y="10" width="14" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 1 1 8 0v3" />
        </svg>
    );
}
function ExternalIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5" aria-hidden="true">
            <path d="M7 17 17 7M7 7h10v10" />
        </svg>
    );
}

/* ─── Desktop dropdown item ─── */
function DropdownLink({ item }) {
    const cls = "block rounded-2xl px-2.5 py-2 text-[13px] font-medium text-slate-700 transition-all duration-200 hover:bg-[var(--site-nav-accent-soft)] hover:text-[var(--site-nav-accent)] focus:bg-[var(--site-nav-accent-soft)] focus:text-[var(--site-nav-accent)] focus:outline-none";
    return item.href
        ? <a href={item.href} className={cls}>{item.label}</a>
        : <Link to={item.to} className={cls}>{item.label}</Link>;
}

/* ─── Desktop nav item ─── */
function NavItem({ item, activeSection }) {
    const isActive = activeSection === item.id;
    const topLinkClass = `inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-[12px] font-semibold leading-none transition-all duration-200 xl:gap-1.5 xl:px-3.5 xl:text-[13px] ${isActive ? "bg-[var(--site-nav-accent-soft)] text-[var(--site-nav-accent)] shadow-[inset_0_0_0_1px_var(--site-nav-accent-border)]" : "text-slate-600 hover:bg-[var(--site-nav-accent-soft)] hover:text-[var(--site-nav-accent)] focus:bg-[var(--site-nav-accent-soft)] focus:text-[var(--site-nav-accent)]"}`;
    return (
        <div className="group relative shrink-0">
            <Link to={item.to} className={topLinkClass}>
                {item.label}
                {item.menu ? <Chevron className="h-3 w-3 transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180" /> : null}
            </Link>
            {item.menu ? (
                <div className="pointer-events-none invisible absolute left-1/2 top-full z-30 hidden min-w-[210px] -translate-x-1/2 pt-3 opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:opacity-100 min-[824px]:block">
                    <div className="rounded-[22px] border border-slate-200/90 bg-white p-4 shadow-[0_24px_60px_rgba(15,27,51,0.14)]">
                        <p className="mb-2.5 px-2.5 text-[11px] font-medium uppercase tracking-normal text-slate-400">{item.menuTitle}</p>
                        <div className="space-y-1">
                            {item.menu.map((child) => <DropdownLink key={child.label} item={child} />)}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/* ─── Mobile: accordion section ─── */
function MobileNavSection({ item, activeSection, onNavigate }) {
    const isActive = activeSection === item.id;

    if (!item.menu) {
        return (
            <Link
                to={item.to}
                onClick={onNavigate}
                className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-[15px] font-semibold leading-snug transition-all duration-200 ${
                    isActive
                        ? "bg-[var(--site-nav-accent-soft)] text-[var(--site-nav-accent)]"
                        : "text-slate-800 hover:bg-slate-50 hover:text-slate-900"
                }`}
            >
                <span className="flex-1">{item.label}</span>
            </Link>
        );
    }

    return (
        <div>
            <Link
                to={item.to}
                onClick={onNavigate}
                className={`flex w-full items-center px-4 py-3.5 text-[15px] font-semibold leading-snug transition-colors duration-200 ${
                    isActive ? "text-[var(--site-nav-accent)]" : "text-slate-800 hover:text-slate-900"
                }`}
            >
                {item.label}
            </Link>

            <div className="px-4 pb-3">

                <div className="relative pl-4">
                    <span
                        className="absolute left-0 top-0 h-full w-px rounded-full"
                        style={{ background: "var(--site-nav-accent)", opacity: 0.18 }}
                        aria-hidden="true"
                    />

                    <div className="space-y-0.5">
                        {item.menu.map((child) =>
                            child.href ? (
                                <a
                                    key={child.label}
                                    href={child.href}
                                    onClick={onNavigate}
                                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-[13.5px] font-medium text-slate-600 transition-all duration-150 hover:bg-white hover:text-[var(--site-nav-accent)]"
                                >
                                    {child.label}
                                </a>
                            ) : (
                                <Link
                                    key={child.label}
                                    to={child.to}
                                    onClick={onNavigate}
                                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-[13.5px] font-medium text-slate-600 transition-all duration-150 hover:bg-white hover:text-[var(--site-nav-accent)]"
                                >
                                    {child.label}
                                </Link>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── Main component ─── */
export default function SiteNavbar({
    activeSection,
    ctaLabel = "Inscribite",
    ctaHref = "https://inscripciones.fisp.org.ar",
    navTheme = null,
    contactEmail = null,
    checkoutMode = false,
    secureLabel = "Inscripción segura · SSL",
    secureBadgeSrc = "",
    secureBadgeAlt = "Inscripción segura",
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const resolvedContactEmail = contactEmail || getPageContactEmail(location.pathname);
    const isEnrollmentCta = ctaLabel === "Inscribite";
    const secureBadge =
        secureBadgeSrc
            ? (
                <img
                    src={secureBadgeSrc}
                    alt={secureBadgeAlt}
                    className="site-navbar-secure-badge"
                />
            )
            : typeof secureLabel === "string"
            ? (
                <>
                    <LockIcon />
                    <span>{secureLabel}</span>
                </>
            )
            : secureLabel;

    const handleEnrollmentNavigation = () => {
        if (location.pathname === "/educativo") {
            const catalogTarget = document.getElementById("catalogo");
            if (catalogTarget) {
                catalogTarget.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            navigate({ pathname: "/educativo", search: location.search, hash: "#catalogo" });
            return;
        }
        if (isProgramDetailPath(location.pathname)) {
            navigate(`${location.pathname}/inscripcion#formulario`);
            return;
        }
        navigate("/educativo");
    };

    const handleCtaNavigation = (event) => {
        if (ctaHref?.startsWith("/")) {
            event.preventDefault();
            navigate(ctaHref);
            return;
        }

        if (!ctaHref?.startsWith("#")) {
            return;
        }

        const target = document.getElementById(ctaHref.slice(1));

        if (target) {
            event.preventDefault();
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!mobileMenuOpen) {
            document.body.style.overflow = "";
            return undefined;
        }
        document.body.style.overflow = "hidden";
        const handleKeyDown = (e) => { if (e.key === "Escape") setMobileMenuOpen(false); };
        const handleResize = () => { if (window.innerWidth >= NAV_COLLAPSE_WIDTH) setMobileMenuOpen(false); };
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", handleResize);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", handleResize);
        };
    }, [mobileMenuOpen]);

    const navbarStyle = {
        "--site-nav-accent": navTheme?.primary || "var(--site-red)",
        "--site-nav-accent-dark": navTheme?.primaryDark || "var(--site-red-dark)",
        "--site-nav-accent-soft": navTheme?.pale || "rgba(200,16,46,0.08)",
        "--site-nav-accent-border": navTheme?.primaryLight || "rgba(200,16,46,0.16)",
        zIndex: "var(--site-header-z)",
    };
    const mobileLayerStyle = { ...navbarStyle, zIndex: 140 };

    return (
        <>
            {/* ── Top bar (desktop only) ── */}
            <div className="site-topbar hidden bg-slate-950 text-white/55 min-[824px]:block">
                <div className="site-header-inner site-header-layout site-topbar-inner">
                    <div className="site-header-left flex items-center gap-2.5">
                        <Link to="/fisp-uba" className="transition-colors duration-200 hover:text-white/85">FISP + UBA</Link>
                        <span className="h-3 w-px bg-white/10" aria-hidden="true" />
                        <a
                            href={CAMPUS_URL}
                            className="site-topbar-campus-link transition-colors duration-200 hover:text-white/85"
                        >
                            Campus Virtual
                        </a>
                    </div>
                    <div className="site-header-right flex items-center gap-3.5">
                        <a href={`mailto:${resolvedContactEmail}`} className="text-white/55">{resolvedContactEmail}</a>
                        <span className="h-3 w-px bg-white/10" aria-hidden="true" />
                        <a href={CONTACT_PHONE_HREF} className="text-white/55">{CONTACT_PHONE_LABEL}</a>
                    </div>
                </div>
            </div>

            {/* ── Main navbar ── */}
            <nav
                className="site-navbar sticky top-0 w-full border-b border-slate-200/80 bg-white backdrop-blur-2xl shadow-[0_8px_20px_rgba(15,27,51,0.06)]"
                style={navbarStyle}
            >
                <div className="site-header-inner site-navbar-inner">
                    <Link to="/" className="flex min-w-0 shrink-0 items-center gap-3">
                        <img src={fispLogo} alt="FISP" className="h-9 w-auto shrink-0" />
                        <span className="hidden max-w-[156px] text-[9px] font-semibold uppercase tracking-[0.16em] leading-[1.12] text-slate-500 min-[1080px]:block">
                            Fundación Iberoamericana de Salud Pública
                        </span>
                    </Link>

                    {checkoutMode ? (
                        <div className="ml-auto inline-flex items-center gap-2 px-0 py-0 text-[12px] font-semibold text-slate-600">
                            {secureBadge}
                        </div>
                    ) : (
                        <>
                            <div className="hidden min-w-0 flex-1 flex-nowrap items-center justify-center gap-1 whitespace-nowrap min-[824px]:flex">
                                {NAV_ITEMS.map((item) => <NavItem key={item.id} item={item} activeSection={activeSection} />)}
                            </div>

                            {ctaLabel ? (
                                isEnrollmentCta ? (
                                    <button
                                        type="button"
                                        onClick={handleEnrollmentNavigation}
                                        className="site-navbar-cta hidden shrink-0 items-center whitespace-nowrap rounded-lg bg-[var(--site-nav-accent)] px-4 py-2.5 text-[13px] font-semibold leading-none !text-white transition-colors duration-200 visited:!text-white hover:bg-[var(--site-nav-accent-dark)] hover:!text-white focus:!text-white focus-visible:!text-white active:!text-white min-[1080px]:inline-flex"
                                    >
                                        {ctaLabel}
                                    </button>
                                ) : (
                                    <a
                                        href={ctaHref}
                                        onClick={handleCtaNavigation}
                                        className="site-navbar-cta hidden shrink-0 items-center whitespace-nowrap rounded-lg bg-[var(--site-nav-accent)] px-4 py-2.5 text-[13px] font-semibold leading-none !text-white transition-colors duration-200 visited:!text-white hover:bg-[var(--site-nav-accent-dark)] hover:!text-white focus:!text-white focus-visible:!text-white active:!text-white min-[1080px]:inline-flex"
                                    >
                                        {ctaLabel}
                                    </a>
                                )
                            ) : null}

                            <button
                                type="button"
                                className="ml-auto inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-[0_10px_24px_rgba(15,27,51,0.08)] transition-all duration-200 hover:border-[var(--site-nav-accent-border)] hover:text-[var(--site-nav-accent)] min-[824px]:hidden"
                                onClick={() => setMobileMenuOpen((o) => !o)}
                                aria-label={mobileMenuOpen ? "Cerrar menu" : "Abrir menu"}
                                aria-expanded={mobileMenuOpen}
                                aria-controls="site-mobile-drawer"
                            >
                                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                            </button>
                        </>
                    )}
                </div>
            </nav>

            {/* ── Mobile drawer ── */}
            {!checkoutMode ? (
                <div
                    className={`site-mobile-nav-layer fixed inset-0 min-[824px]:hidden ${mobileMenuOpen ? "pointer-events-auto" : "pointer-events-none"}`}
                    style={mobileLayerStyle}
                >
                    {/* Backdrop */}
                    <button
                        type="button"
                        className={`absolute inset-0 bg-slate-950/50 transition-opacity duration-300 ${mobileMenuOpen ? "opacity-100" : "opacity-0"}`}
                        aria-label="Cerrar menu lateral"
                        onClick={() => setMobileMenuOpen(false)}
                    />

                    {/* Drawer panel */}
                    <aside
                        id="site-mobile-drawer"
                        className={`site-mobile-nav absolute right-0 top-0 flex h-full w-[min(88vw,380px)] max-w-full flex-col bg-white shadow-[-32px_0_60px_rgba(15,27,51,0.18)] transition-transform duration-300 ${
                            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
                        }`}
                    >
                        {/* Drawer header */}
                        <div className="flex h-[64px] shrink-0 items-center justify-between border-b border-slate-100 px-5">
                            <div></div>
                            <button
                                type="button"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-all duration-200 hover:border-[var(--site-nav-accent-border)] hover:text-[var(--site-nav-accent)]"
                                aria-label="Cerrar menu"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <CloseIcon />
                            </button>
                        </div>

                        {/* Nav items */}
                        <div className="flex-1 overflow-y-auto px-4 py-4">
                            <div className="space-y-1">
                                {NAV_ITEMS.map((item) => (
                                    <MobileNavSection
                                        key={`mobile-${item.id}`}
                                        item={item}
                                        activeSection={activeSection}
                                        onNavigate={() => setMobileMenuOpen(false)}
                                    />
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            ) : null}
        </>
    );
}
